import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import { Toaster } from '@/components/ui/sonner';
import LoginPage from '@/pages/LoginPage';
import CadastroAlunoPage from '@/pages/public/CadastroAlunoPage';
import BlockedScreen from '@/pages/BlockedScreen';
import DashboardLayout from '@/components/DashboardLayout';
import ParceiroLayout from '@/components/ParceiroLayout';
import ParceiroTurmasPage from '@/pages/parceiro/ParceiroTurmasPage';
import ParceiroFinanceiroPage from '@/pages/parceiro/ParceiroFinanceiroPage';
import ParceiroPerfilPage from '@/pages/parceiro/ParceiroPerfilPage';
import ParceiroAlterarSenhaPage from '@/pages/parceiro/ParceiroAlterarSenhaPage';
import AdminDashboard from '@/pages/admin/AdminDashboard';
import GestoresPage from '@/pages/admin/GestoresPage';
import PlanosPlataformaPage from '@/pages/admin/PlanosPlataformaPage';
import AdminFinanceiroPage from '@/pages/admin/AdminFinanceiroPage';
import AdminConfigPixPage from '@/pages/admin/AdminConfigPixPage';
import GestorDashboard from '@/pages/gestor/GestorDashboard';
import ProfessorDashboard from '@/pages/gestor/ProfessorDashboard';
import ContratosPage from '@/pages/gestor/ContratosPage';
import AlunosPage from '@/pages/gestor/AlunosPage';
import ResponsaveisPage from '@/pages/gestor/ResponsaveisPage';
import CategoriasPage from '@/pages/gestor/CategoriasPage';
import TurmasPage from '@/pages/gestor/TurmasPage';
import TreinosPage from '@/pages/gestor/TreinosPage';
import SeletivasPage from '@/pages/gestor/SeletivasPage';
import PresencaPage from '@/pages/gestor/PresencaPage';
import PlanosAlunoPage from '@/pages/gestor/PlanosAlunoPage';
import ColaboradoresPage from '@/pages/gestor/ColaboradoresPage';
import ParceriasPage from '@/pages/gestor/ParceriasPage';
import InadimplentesPage from '@/pages/gestor/InadimplentesPage';
import FinanceiroPage from '@/pages/gestor/FinanceiroPage';
import RecebimentosSociosPage from '@/pages/gestor/RecebimentosSociosPage';
import MensalidadesPage from '@/pages/gestor/MensalidadesPage';
import DespesasPage from '@/pages/gestor/DespesasPage';
import PreCadastrosPage from "@/pages/gestor/PreCadastrosPage";
import MeuFinanceiroPage from '@/pages/gestor/MeuFinanceiroPage';
import ConfiguracoesPage from '@/pages/gestor/ConfiguracoesPage';
import ProdutosPage from '@/pages/gestor/ProdutosPage';
import PedidosGestorPage from '@/pages/gestor/PedidosGestorPage';
import AvisosPage from '@/pages/gestor/AvisosPage';
import AlunoDashboardPage from '@/pages/aluno/AlunoDashboardPage';
import AlunoMensalidadesPage from '@/pages/aluno/AlunoMensalidadesPage';
import AlunoRankingPage from '@/pages/aluno/AlunoRankingPage';
import AlunoMedalhasPage from '@/pages/aluno/AlunoMedalhasPage';
import AlunoLojaPage from '@/pages/aluno/AlunoLojaPage';
import AlunoCarrinhoPage from '@/pages/aluno/AlunoCarrinhoPage';
import AlunoPedidosPage from '@/pages/aluno/AlunoPedidosPage';
import AlterarSenhaPage from '@/pages/AlterarSenhaPage';
import MeuPerfilPage from '@/pages/MeuPerfilPage';
import AlunoPerfilPage from '@/pages/aluno/AlunoPerfilPage';
import AlunoResponsavelPage from '@/pages/aluno/AlunoResponsavelPage';
import ResponsavelPortalPage from '@/pages/responsavel/ResponsavelPortalPage';
import AlunoTreinosPage from '@/pages/aluno/AlunoTreinosPage';
import TreinosCasaPage from '@/pages/gestor/TreinosCasaPage';
import AlunoTreinosCasaPage from '@/pages/aluno/AlunoTreinosCasaPage';
import AlunoTreinoCasaExecutarPage from '@/pages/aluno/AlunoTreinoCasaExecutarPage';
import SelecaoSemana from '@/pages/aluno/SelecaoSemana';
import { Loader2 } from 'lucide-react';
import '@/App.css';

function LoadingScreen() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0A0A0A]" data-testid="loading-screen">
      <div className="text-center space-y-4">
        <Loader2 className="w-8 h-8 animate-spin text-[#007AFF] mx-auto" />
        <p className="text-zinc-400 text-sm">Carregando...</p>
      </div>
    </div>
  );
}

function AppRoutes() {
  const { user, loading, effectiveRole } = useAuth();

  if (
    window.location.pathname === '/cadastro-aluno' ||
    window.location.pathname.startsWith('/pre-cadastro/')
  ) {
    return (
      <Routes>
        <Route path="/cadastro-aluno" element={<CadastroAlunoPage />} />
        <Route
          path="/pre-cadastro/:token"
          element={<CadastroAlunoPage />}
        />
      </Routes>
    );
  }

  if (loading) return <LoadingScreen />;

  if (!user) {
    return (
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/cadastro-aluno" element={<CadastroAlunoPage />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  if (user.blocked) {
    return (
      <Routes>
        <Route path="/blocked" element={<BlockedScreen />} />
        <Route path="*" element={<Navigate to="/blocked" replace />} />
      </Routes>
    );
  }

  if (user.role === 'admin') {
    return (
      <Routes>
        <Route element={<DashboardLayout />}>
          <Route path="/admin" element={<AdminDashboard />} />
          <Route path="/admin/gestores" element={<GestoresPage />} />
          <Route path="/admin/planos" element={<PlanosPlataformaPage />} />
          <Route path="/admin/financeiro" element={<AdminFinanceiroPage />} />
          <Route path="/admin/config-pix" element={<AdminConfigPixPage />} />
          <Route path="/admin/alterar-senha" element={<AlterarSenhaPage />} />
          <Route path="/admin/perfil" element={<MeuPerfilPage />} />
        </Route>
        <Route path="*" element={<Navigate to="/admin" replace />} />
      </Routes>
    );
  }

  if (
    user.role === 'aluno' &&
    user.acesso_staff === true &&
    effectiveRole === 'professor_staff'
  ) {
    return (
      <Routes>
        <Route element={<DashboardLayout />}>
          <Route path="/gestor" element={<ProfessorDashboard />} />
          <Route path="/gestor/turmas" element={<TurmasPage />} />
          <Route path="/gestor/treinos" element={<TreinosPage />} />
          <Route path="/gestor/treinos-casa" element={<TreinosCasaPage />} />
          <Route path="/gestor/seletivas" element={<SeletivasPage />} />
          <Route path="/gestor/presenca" element={<PresencaPage />} />
        </Route>
        <Route path="*" element={<Navigate to="/gestor" replace />} />
      </Routes>
    );
  }

  if (user.role === 'aluno') {
    return (
      <Routes>
        <Route element={<DashboardLayout />}>
          <Route path="/aluno" element={<AlunoDashboardPage />} />
          <Route path="/aluno/treinos" element={<AlunoTreinosPage />} />
          <Route path="/aluno/treinos-casa" element={<AlunoTreinosCasaPage />} />
          <Route path="/aluno/treinos-casa/:treinoCasaId" element={<AlunoTreinoCasaExecutarPage />} />
          <Route path="/aluno/mensalidades" element={<AlunoMensalidadesPage />} />
          <Route path="/aluno/ranking" element={<AlunoRankingPage />} />
          <Route path="/aluno/medalhas" element={<AlunoMedalhasPage />} />
          <Route path="/aluno/loja" element={<AlunoLojaPage />} />
          <Route path="/aluno/carrinho" element={<AlunoCarrinhoPage />} />
          <Route path="/aluno/pedidos" element={<AlunoPedidosPage />} />
          <Route path="/aluno/alterar-senha" element={<AlterarSenhaPage />} />
          <Route path="/aluno/responsavel" element={<AlunoResponsavelPage />} />
          <Route path="/aluno/perfil" element={<AlunoPerfilPage />} />
          <Route path="/aluno/selecao-semana" element={<SelecaoSemana />} />
</Route>
        <Route path="*" element={<Navigate to="/aluno" replace />} />
      </Routes>
    );
  }

  if (user.role === 'responsavel') {

    return (

      <Routes>

        <Route element={<DashboardLayout />}>

          <Route

            path="/responsavel"

            element={<ResponsavelPortalPage />}

          />

          <Route

            path="/responsavel/alterar-senha"

            element={<AlterarSenhaPage />}

          />

        </Route>

        <Route

          path="*"

          element={<Navigate to="/responsavel" replace />}

        />

      </Routes>

    );

  }


  if (user.role === 'parceiro') {
    return (
      <Routes>
        <Route element={<ParceiroLayout />}>
          <Route path="/parceiro" element={<Navigate to="/parceiro/turmas" replace />} />
          <Route path="/parceiro/turmas" element={<ParceiroTurmasPage />} />
          <Route path="/parceiro/financeiro" element={<ParceiroFinanceiroPage />} />
          <Route path="/parceiro/alterar-senha" element={<ParceiroAlterarSenhaPage />} />
          <Route path="/parceiro/perfil" element={<ParceiroPerfilPage />} />
        </Route>
        <Route path="*" element={<Navigate to="/parceiro/turmas" replace />} />
      </Routes>
    );
  }

  // gestor, professor, financeiro
  return (
    <Routes>
      <Route element={<DashboardLayout />}>
        <Route
            path="/gestor"
            element={
              user?.role === 'professor'
                ? <ProfessorDashboard />
                : <GestorDashboard />
            }
          />
        <Route path="/gestor/contratos" element={<ContratosPage />} />
        <Route path="/gestor/alunos" element={<AlunosPage />} />
        <Route path="/gestor/responsaveis" element={<ResponsaveisPage />} />
          <Route path="/gestor/categorias" element={<CategoriasPage />} />
        <Route path="/gestor/turmas" element={<TurmasPage />} />
        <Route path="/gestor/treinos" element={<TreinosPage />} />
        <Route path="/gestor/treinos-casa" element={<TreinosCasaPage />} />
        <Route path="/gestor/seletivas" element={<SeletivasPage />} />
        <Route path="/gestor/presenca" element={<PresencaPage />} />
        <Route path="/gestor/avisos" element={<AvisosPage />} />
        <Route path="/gestor/planos" element={<PlanosAlunoPage />} />
        <Route path="/gestor/colaboradores" element={<ColaboradoresPage />} />
          <Route path="/gestor/parcerias" element={<ParceriasPage />} />
        <Route path="/gestor/inadimplentes" element={<InadimplentesPage />} />
        <Route path="/gestor/financeiro" element={<FinanceiroPage />} />
        <Route path="/gestor/recebimentos-socios" element={<RecebimentosSociosPage />} />
        <Route
          path="/gestor/pre-cadastros"
          element={
            ['gestor', 'socio'].includes(user?.role)
              ? <PreCadastrosPage />
              : <Navigate to="/gestor" replace />
          }
        />
        <Route path="/gestor/mensalidades" element={<MensalidadesPage />} />
        <Route path="/gestor/despesas" element={<DespesasPage />} />
        <Route path="/gestor/meu-financeiro" element={<MeuFinanceiroPage />} />
        <Route path="/gestor/configuracoes" element={<ConfiguracoesPage />} />
        <Route path="/gestor/produtos" element={<ProdutosPage />} />
        <Route path="/gestor/pedidos" element={<PedidosGestorPage />} />
        <Route path="/gestor/alterar-senha" element={<AlterarSenhaPage />} />
        <Route path="/gestor/perfil" element={<MeuPerfilPage />} />
      </Route>
      <Route path="*" element={<Navigate to="/gestor" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <Toaster theme="dark" position="top-right" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
